import type { BrowserOptions } from '@sentry/angular';

/**
 * Strips client personal data out of URLs before anything reaches Sentry.
 *
 * Several API routes carry personal data in the URL itself, e.g.
 *   GET /stylists/:id/clients/search/:term     (client name, phone or email)
 *   GET /salons/:id/clients/emailchecker/:email
 *   GET /stylists/emailchecker/:email
 *   GET /stylists/search/:term
 *   GET /sales?searchTerm=...
 * and the password-reset page route carries a reset token.
 *
 * Those URLs reach Sentry as xhr/fetch/navigation breadcrumbs (which Session
 * Replay also records, after `beforeBreadcrumb` has run), as http.client span
 * descriptions and data, as transaction names, and inside HttpErrorResponse
 * messages ("Http failure response for <url>: 500 ..."). Scrubbing at the
 * Sentry layer covers every current and future call site without changing
 * the API's URL design.
 */

export const FILTERED = '[Filtered]';

// A path remainder or query value ends at the next URL delimiter, a newline,
// or the ": " Angular puts after the URL in HttpErrorResponse messages
// ("Http failure response for <url>: 500 ..."). A space is deliberately NOT a
// terminator: client search terms are interpolated unencoded, so "jane doe"
// can appear in a raw URL with a literal space.
const PATH_END = '(?=[?#\\n]|: |$)';
const QUERY_VALUE_END = '(?=[&#\\n]|: |$)';

// Path segments whose remainder (up to the query string) is personal data or a secret.
const SENSITIVE_PATH = new RegExp(
  `(/(?:search|emailchecker|password-reset-password)/)[^?#\\n]*?${PATH_END}`,
  'gi'
);

// Query parameters whose value is personal data or a secret.
const SENSITIVE_PARAMS = [
  'searchTerm',
  'search',
  'term',
  'q',
  'query',
  'email',
  'phone',
  'mobile',
  'token',
  'code',
  'state',
];
const SENSITIVE_QUERY = new RegExp(
  `([?&](?:${SENSITIVE_PARAMS.join('|')})=)[^&#\\n]*?${QUERY_VALUE_END}`,
  'gi'
);

// Anything that looks like an email address, raw or percent-encoded, anywhere.
// Quantifiers are bounded (RFC local-part/domain limits) and the local part
// excludes "%" so that a long run like "a%40a%40..." cannot backtrack
// super-linearly: these regexes run on the main thread for every breadcrumb.
const EMAIL = /[^\s/?#&=:@%"'<>()[\]]{1,64}(?:@|%40)[^\s/?#&=:@"'<>()[\]]{1,253}\.[a-z]{2,24}/gi;

// International-format phone numbers (+1 604 555 1234, %2B16045551234) and
// (604) 555-1234. Plain digit runs are left alone: they are record IDs.
const PHONE = /(?:\+|%2B)\d(?:[\d\s().-]|%20){6,}\d|\(\d{3}\)\s?\d{3}[-\s]\d{4}/gi;

/** Redacts personal data from a URL or from free text that contains URLs. */
export function scrubUrl<T>(value: T): T {
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }
  return value
    .replace(SENSITIVE_PATH, `$1${FILTERED}`)
    .replace(SENSITIVE_QUERY, `$1${FILTERED}`)
    .replace(EMAIL, FILTERED)
    .replace(PHONE, FILTERED) as unknown as T;
}

/** Scrubs every string value of a flat record (span data, breadcrumb data). */
function scrubRecord<R extends Record<string, unknown> | undefined>(record: R): R {
  if (!record) {
    return record;
  }
  for (const key of Object.keys(record)) {
    (record as Record<string, unknown>)[key] = scrubUrl(record[key]);
  }
  return record;
}

type BeforeBreadcrumb = NonNullable<BrowserOptions['beforeBreadcrumb']>;
type SentryErrorEvent = Parameters<NonNullable<BrowserOptions['beforeSend']>>[0];
type SentryTransactionEvent = Parameters<NonNullable<BrowserOptions['beforeSendTransaction']>>[0];
type BeforeSendSpan = NonNullable<BrowserOptions['beforeSendSpan']>;

export const scrubBreadcrumb: BeforeBreadcrumb = (breadcrumb) => {
  breadcrumb.message = scrubUrl(breadcrumb.message);
  scrubRecord(breadcrumb.data);
  return breadcrumb;
};

export const scrubSpan: BeforeSendSpan = (span) => {
  span.description = scrubUrl(span.description);
  scrubRecord(span.data);
  return span;
};

/** Fields shared by error and transaction events that can carry a URL. */
function scrubEventCommon<E extends SentryErrorEvent | SentryTransactionEvent>(event: E): E {
  event.message = scrubUrl(event.message);
  event.transaction = scrubUrl(event.transaction);
  if (event.request) {
    event.request.url = scrubUrl(event.request.url);
    scrubRecord(event.request.headers); // Referer is the previous page URL
    const query = event.request.query_string;
    if (typeof query === 'string' && query) {
      // A bare query string has no leading "?" for the param rule to anchor on.
      event.request.query_string = scrubUrl(`?${query}`).slice(1);
    }
  }
  for (const exception of event.exception?.values ?? []) {
    exception.value = scrubUrl(exception.value);
  }
  for (const breadcrumb of event.breadcrumbs ?? []) {
    scrubBreadcrumb(breadcrumb);
  }
  scrubRecord(event.contexts?.trace?.data as Record<string, unknown> | undefined);
  return event;
}

export const scrubErrorEvent = (event: SentryErrorEvent): SentryErrorEvent => scrubEventCommon(event);

/**
 * Child spans have already been through beforeSendSpan; the root span's name
 * and data live on the transaction name and trace context instead.
 */
export const scrubTransactionEvent = (event: SentryTransactionEvent): SentryTransactionEvent =>
  scrubEventCommon(event);

/**
 * Session Replay events bypass beforeSend, but do run through scope event
 * processors. Their `urls` list is every page URL visited in the session
 * (e.g. the password-reset route with its token). Register once after init.
 *
 * Not covered here: the replay *recording* itself (history `navigation.push`
 * entries and the recorded `href`). That needs `beforeAddRecordingEvent` in
 * the replay integration options.
 */
export function scrubReplayEventUrls<E extends { type?: string; urls?: unknown }>(event: E): E {
  if (event.type === 'replay_event' && Array.isArray(event.urls)) {
    event.urls = event.urls.map((url) => scrubUrl(url));
  }
  return event;
}

/** Spread into Sentry.init so every event, span and breadcrumb is scrubbed. */
export const SENTRY_PII_SCRUB_OPTIONS = {
  beforeBreadcrumb: scrubBreadcrumb,
  beforeSend: scrubErrorEvent,
  beforeSendTransaction: scrubTransactionEvent,
  beforeSendSpan: scrubSpan,
} satisfies Partial<BrowserOptions>;
